import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getPDV, obterConstrutor } from '~/Services/Service';
import { customStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';

// Campos administrativos exigidos pelo payload da venda real:
// codigo_usuario (corretor responsável pela venda), codigo_construtor,
// codigo_ponto_venda, venda_interna, contrato_digital, opcao_recebimento_boletos
// (is_parcelas_nao_concomitantes mora no fluxo de simulação — afeta o cálculo da venda)
//
// Modos do select de Corretor (W-0075):
// - 'editavel' (ADM com característica 673): escolhe qualquer corretor da lista
// - 'readonly' (próprio corretor logado): mostra nome, não permite trocar
// - 'oculto' (sem permissão): não renderiza o select (botão Gerar Venda fica bloqueado)
export default function AdminCampos(props) {
  const {
    codigoFamilia,
    codigoConstrutor,
    setCodigoConstrutor,
    codigoPontoVenda,
    setCodigoPontoVenda,
    vendaInterna,
    setVendaInterna,
    contratoDigital,
    setContratoDigital,
    opcaoRecebimentoBoletos,
    setOpcaoRecebimentoBoletos,
    corretorAtivo,
    setCorretorAtivo,
    opcoesCorretores,
    modoCorretor,
  } = props;
  // Switch: false (1) = Físico e Digital, true (3) = Somente Digital
  const somenteDigital = opcaoRecebimentoBoletos === 3;

  const [pdvs, setPdvs] = useState([]);
  const [construtores, setConstrutores] = useState([]);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!codigoFamilia) return;
    setCarregando(true);
    Promise.all([
      getPDV().catch(() => []),
      obterConstrutor().catch(() => []),
    ])
      .then((res) => {
        const pdvList = Array.isArray(res[0]) ? res[0] : (res[0] && res[0].lista) || [];
        const consList = Array.isArray(res[1]) ? res[1] : (res[1] && res[1].lista) || [];
        setPdvs(pdvList);
        setConstrutores(consList);
      })
      .catch((err) => {
        toast.error('Erro ao carregar PDVs/construtores: ' + (err.message || ''));
      })
      .finally(() => setCarregando(false));
  }, [codigoFamilia]);

  const pdvOptions = useMemo(() => pdvs.map((p) => ({
    value: p.codigo || p.codigo_ponto_venda || p.codigoPontoVenda,
    label: (p.descricao || p.nome || p.codigo) + '',
  })), [pdvs]);

  const construtorOptions = useMemo(() => construtores.map((c) => ({
    value: c.codigo || c.codigo_construtor,
    label: (c.descricao || c.nome || c.codigo) + '',
  })), [construtores]);

  const corretorOptions = useMemo(() => (opcoesCorretores || []).map((c) => {
    const codigo = c.codigo || c.Codigo;
    return {
      value: codigo,
      label: c.descricao || c.nome || c.Descricao || c.Nome || String(codigo),
    };
  }), [opcoesCorretores]);

  const pdvSelecionado = useMemo(() =>
    pdvOptions.find((o) => o.value === codigoPontoVenda) || null,
    [pdvOptions, codigoPontoVenda]);

  const construtorSelecionado = useMemo(() =>
    construtorOptions.find((o) => o.value === codigoConstrutor) || null,
    [construtorOptions, codigoConstrutor]);

  const corretorSelecionado = useMemo(() =>
    corretorOptions.find((o) => o.value === corretorAtivo) || null,
    [corretorOptions, corretorAtivo]);

  return (
    <Card className="mb-2">
      <Card.Body className="py-2">
        <h6 className="mb-2 text-primary"><strong>Dados da Venda</strong></h6>

        <Row>
          {modoCorretor !== 'oculto' && (
            <Col md={4} xs={12}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Corretor</Form.Label>
                {modoCorretor === 'editavel' ? (
                  <Select
                    placeholder="Selecione o corretor..."
                    options={corretorOptions}
                    value={corretorSelecionado}
                    onChange={(opt) => setCorretorAtivo(opt ? opt.value : null)}
                    isClearable
                    styles={customStylesSm}
                    components={{ DropdownIndicator }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    noOptionsMessage={() => 'Nenhum corretor encontrado'}
                  />
                ) : (
                  <Form.Control
                    size="sm"
                    readOnly
                    value={corretorSelecionado ? corretorSelecionado.label : '—'}
                  />
                )}
              </Form.Group>
            </Col>
          )}
          <Col md={modoCorretor !== 'oculto' ? 4 : 6} xs={12}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Construtor</Form.Label>
              <Select
                placeholder={carregando ? 'Carregando...' : 'Selecione...'}
                options={construtorOptions}
                value={construtorSelecionado}
                onChange={(opt) => setCodigoConstrutor(opt ? opt.value : null)}
                isLoading={carregando}
                styles={customStylesSm}
                components={{ DropdownIndicator }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
          <Col md={modoCorretor !== 'oculto' ? 4 : 6} xs={12}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Ponto de Venda (PDV)</Form.Label>
              <Select
                placeholder={carregando ? 'Carregando...' : 'Selecione...'}
                options={pdvOptions}
                value={pdvSelecionado}
                onChange={(opt) => setCodigoPontoVenda(opt ? opt.value : null)}
                isLoading={carregando}
                styles={customStylesSm}
                components={{ DropdownIndicator }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md={3} xs={6}>
            <Form.Check
              type="checkbox"
              id="venda-interna"
              label={<small>Venda interna</small>}
              checked={!!vendaInterna}
              onChange={(e) => setVendaInterna(e.target.checked)}
            />
          </Col>
          <Col md={3} xs={6}>
            <Form.Check
              type="checkbox"
              id="contrato-digital"
              label={<small>Contrato digital</small>}
              checked={!!contratoDigital}
              onChange={(e) => setContratoDigital(e.target.checked)}
            />
          </Col>
          <Col md={6} xs={12}>
            <Form.Check
              type="switch"
              id="tipo-boleto"
              label={<small>Tipo de boleto: <strong>{somenteDigital ? 'Somente Digital' : 'Físico e Digital'}</strong></small>}
              checked={somenteDigital}
              onChange={(e) => setOpcaoRecebimentoBoletos(e.target.checked ? 3 : 1)}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
